import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import AppButton from "../../../common/AppButton";

export type EditEntityField = {
  key: string;
  label: string;
  value: string;
  multiline?: boolean;
};

type EditEntityDialogProps = {
  open: boolean;
  title: string;
  fields: EditEntityField[];
  loading?: boolean;
  onClose: () => void;
  onSave: (values: Record<string, string>) => void | Promise<void>;
};

const EditEntityDialog: React.FC<EditEntityDialogProps> = ({
  open,
  title,
  fields,
  loading = false,
  onClose,
  onSave
}) => {
  const { t } = useKivunovaTranslation();
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues(
        Object.fromEntries(fields.map((field) => [field.key, field.value]))
      );
    }
  }, [open, fields]);

  const handleClose = () => {
    onClose();
  };

  const requiredFilled = fields.every((field) => {
    if (field.key === "name") {
      return values[field.key]?.trim().length > 0;
    }
    return true;
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {fields.map((field) => (
          <TextField
            key={field.key}
            fullWidth
            sx={{ mt: 2 }}
            label={field.label}
            value={values[field.key] ?? ""}
            multiline={field.multiline}
            minRows={field.multiline ? 2 : undefined}
            onChange={(event) =>
              setValues((prev) => ({
                ...prev,
                [field.key]: event.target.value
              }))
            }
          />
        ))}
      </DialogContent>
      <DialogActions>
        <AppButton variant="text" onClick={handleClose} disabled={loading}>
          {t("cancel", "Cancel")}
        </AppButton>
        <AppButton
          disabled={loading || !requiredFilled}
          onClick={async () => {
            await onSave(values);
          }}
        >
          {t("save", "Save")}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditEntityDialog;
